import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  Children,
  isValidElement
} from 'react'
import { createPortal } from 'react-dom'
import { cm } from '~/utils/utils'
import {
  SelectComponentDownCaretIcon,
  SelectComponentDownDisabledCaretIcon,
  SelectCheckIcon
} from '../icons'

export type SelectorVariant = 'default' | 'labeled'
export type SelectorSize = 'small' | 'default' | 'large'

export interface SelectorOption {
  key: string
  value: string
  label: string
}

export interface SelectorItemProps {
  key?: string
  value?: string
  children: React.ReactNode
  disabled?: boolean
  className?: string
}

export interface SelectorProps {
  variant?: SelectorVariant
  size?: SelectorSize
  label?: string
  placeholder?: string
  options?: SelectorOption[]
  value?: string
  onSelectionChange?: (value: string) => void
  disabled?: boolean
  disabledText?: string
  className?: string
  triggerClassName?: string
  labelClassName?: string
  valueClassName?: string
  dropdownClassName?: string
  optionClassName?: string
  children?: React.ReactNode
  'aria-label'?: string
}

export const Selector = React.forwardRef<HTMLDivElement, SelectorProps>(
  (
    {
      variant = 'default',
      size = 'default',
      label,
      placeholder = 'Select',
      options,
      value,
      onSelectionChange,
      disabled = false,
      disabledText = 'Unavailable',
      className,
      triggerClassName,
      labelClassName,
      valueClassName,
      dropdownClassName,
      optionClassName,
      children,
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) => {
    // Extract option data from children
    const childrenOptions = React.useMemo(() => {
      if (!children) return []

      const items: SelectorOption[] = []
      Children.forEach(children, (child, index) => {
        if (isValidElement(child) && child.type === SelectItem) {
          const itemProps = child.props as SelectorItemProps
          const reactKey = child.key ?? `select-item-${index}`
          const uniqueKey = typeof reactKey === 'string' ? reactKey : `select-item-${index}`
          const uniqueValue = itemProps.value || uniqueKey
          items.push({
            key: uniqueKey,
            value: uniqueValue,
            label:
              typeof itemProps.children === 'string'
                ? itemProps.children
                : String(itemProps.children)
          })
        }
      })
      return items
    }, [children])

    // Prefer children, fall back to options
    const finalOptions = children ? childrenOptions : options || []

    // An empty options list is functionally a disabled selector — there is
    // nothing to pick. Treat it visually the same so callers don't need to
    // pass `disabled` themselves on top of the empty list.
    const isEffectivelyDisabled = disabled || finalOptions.length === 0

    const [searchQuery, setSearchQuery] = useState('')
    const searchInputRef = useRef<HTMLInputElement>(null)

    // Filter options by search query
    const displayedOptions = useMemo(() => {
      if (!searchQuery.trim()) return finalOptions
      const q = searchQuery.toLowerCase()
      return finalOptions.filter(opt => opt.label.toLowerCase().includes(q))
    }, [searchQuery, finalOptions])

    const [isOpen, setIsOpen] = useState(false)

    // Clear stale search query when available options change to avoid
    // showing an empty dropdown if the previous query no longer matches
    useEffect(() => {
      if (searchQuery && finalOptions.length > 0) {
        const q = searchQuery.toLowerCase()
        const hasMatch = finalOptions.some(opt => opt.label.toLowerCase().includes(q))
        if (!hasMatch) setSearchQuery('')
      }
    }, [finalOptions]) // eslint-disable-line react-hooks/exhaustive-deps

    // Close dropdown if options become empty while open (e.g. data refetch)
    useEffect(() => {
      if (isOpen && finalOptions.length === 0) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }, [isOpen, finalOptions.length])
    const [selectedOption, setSelectedOption] = useState<SelectorOption | null>(null)
    const triggerRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const [dropdownPosition, setDropdownPosition] = useState<{
      top: number
      left: number
      width: number
    }>({ top: 0, left: 0, width: 0 })

    // Update the selected item when the external value or finalOptions change
    useEffect(() => {
      if (value !== undefined && finalOptions.length > 0) {
        const option = finalOptions.find(opt => opt.value === value)
        setSelectedOption(option || null)
      } else if (!value) {
        setSelectedOption(null)
      }
    }, [value, finalOptions])

    // Calculate dropdown position synchronously before paint to avoid flash at {0,0}
    useLayoutEffect(() => {
      if (isOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        })
      }
    }, [isOpen])

    // Keep position updated on resize/scroll
    useEffect(() => {
      if (!isOpen) return
      function updatePosition() {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect()
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width
          })
        }
      }
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition, true)
      return () => {
        window.removeEventListener('resize', updatePosition)
        window.removeEventListener('scroll', updatePosition, true)
      }
    }, [isOpen])

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          triggerRef.current &&
          !triggerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false)
          setSearchQuery('')
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [])

    // Auto-focus search input when dropdown opens
    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        const timer = setTimeout(() => searchInputRef.current?.focus(), 0)
        return () => clearTimeout(timer)
      }
    }, [isOpen])

    const handleToggle = () => {
      if (!disabled && finalOptions.length > 0) {
        const willOpen = !isOpen
        // Always clear search query on toggle to prevent stale filter state
        setSearchQuery('')
        setIsOpen(willOpen)
        if (!willOpen) {
          searchInputRef.current?.blur()
        }
      }
    }

    const handleOptionSelect = (option: SelectorOption) => {
      const isDeselecting = selectedOption?.key === option.key
      setSelectedOption(isDeselecting ? null : option)
      setIsOpen(false)
      setSearchQuery('')
      searchInputRef.current?.blur()
      onSelectionChange?.(isDeselecting ? '' : option.value)
    }

    const getSizeStyles = () => {
      switch (size) {
        case 'small':
          return 'w-[160px]'
        case 'large':
          return 'w-[360px]'
        case 'default':
          return 'w-[240px]'
        default:
          return 'w-full'
      }
    }

    return (
      <div>
        <div
          ref={ref}
          className={cm(
            'flex flex-col items-start justify-center h-[48px]',
            getSizeStyles(),
            className
          )}
          {...props}
        >
          <div
            ref={triggerRef}
            role='combobox'
            aria-label={ariaLabel || label || placeholder}
            aria-expanded={isOpen}
            aria-haspopup='listbox'
            aria-disabled={isEffectivelyDisabled}
            className={cm(
              'w-full h-12 px-4 pt-4 pb-3 border rounded-[12px] bg-white flex items-center justify-between cursor-pointer transition-colors select-none',
              isEffectivelyDisabled
                ? 'bg-[#F5F5F5] cursor-not-allowed border-[#D6D6D6]'
                : isOpen
                  ? 'border-[#846FE1]'
                  : 'border-[#D6D6D6]',
              triggerClassName
            )}
            onClick={handleToggle}
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleToggle()
              } else if (e.key === 'Escape') {
                setIsOpen(false)
              }
            }}
          >
            <div className='flex-1 min-w-0'>
              {variant === 'labeled' && label && (
                <div
                  className={cm(
                    'flex flex-col justify-center font-semibold font-mono truncate select-none tracking-[-0.33px]',
                    isEffectivelyDisabled ? 'text-[rgba(38,38,38,0.20)]' : 'text-black/45',
                    isOpen || selectedOption
                      ? 'text-[11px] leading-[14px] tracking-[-0.33px]'
                      : 'text-[12px] leading-normal',
                    labelClassName
                  )}
                >
                  {label}
                </div>
              )}
              <div
                className={cm(
                  'relative',
                  variant === 'labeled' && label && !selectedOption && !isOpen && 'hidden'
                )}
              >
                <input
                  ref={searchInputRef}
                  type='text'
                  readOnly={!isOpen}
                  className={cm(
                    'text-[14px] font-medium font-sans leading-normal bg-transparent outline-none w-full truncate p-0 m-0 border-0',
                    isEffectivelyDisabled
                      ? 'text-[rgba(38,38,38,0.20)]'
                      : 'text-[rgba(0,0,0,0.88)]',
                    valueClassName,
                    isOpen ? 'cursor-text' : 'cursor-pointer pointer-events-none'
                  )}
                  style={{ userSelect: isOpen ? 'text' : 'none' }}
                  placeholder=''
                  value={
                    isOpen
                      ? searchQuery
                      : finalOptions.length === 0
                        ? disabledText
                        : selectedOption?.label ||
                          (variant === 'labeled' && label ? '' : placeholder)
                  }
                  onChange={e => {
                    if (isOpen) setSearchQuery(e.target.value)
                  }}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      setIsOpen(false)
                      setSearchQuery('')
                    }
                    if (e.key === 'Enter') e.preventDefault()
                    e.stopPropagation()
                  }}
                  data-testid='search-input'
                />
                {/* Custom placeholder overlay — a div always truncates, unlike a focused input's native placeholder */}
                {isOpen && !searchQuery && (
                  <div className='absolute inset-0 flex items-center pointer-events-none text-[14px] font-medium font-sans leading-normal text-[rgba(0,0,0,0.25)] truncate select-none'>
                    {selectedOption ? selectedOption.label : 'Search'}
                  </div>
                )}
              </div>
            </div>

            <div className='w-5 h-5 ml-2 flex items-center justify-center select-none'>
              {isEffectivelyDisabled ? (
                <SelectComponentDownDisabledCaretIcon />
              ) : (
                <SelectComponentDownCaretIcon />
              )}
            </div>
          </div>
        </div>

        {isOpen &&
          typeof window !== 'undefined' &&
          createPortal(
            <div
              ref={dropdownRef}
              className={cm(
                'absolute bg-white rounded-[16px] shadow-popover z-[9999] p-2 flex flex-col gap-2 max-h-[300px] overflow-y-auto overflow-x-hidden',
                dropdownClassName
              )}
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width || undefined,
                // Hide until position is calculated to prevent flash at top-left corner
                visibility:
                  dropdownPosition.width > 0 || dropdownPosition.top > 0 ? 'visible' : 'hidden',
                zIndex: 9999
              }}
              onMouseDown={e => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              {displayedOptions.map(option => {
                const isSelected = selectedOption?.key === option.key
                return (
                  <div
                    key={option.key}
                    className={cm(
                      'leading-[16px] h-8 shrink-0 w-full rounded-[8px] gap-2 truncate px-3 flex items-center justify-between text-[14px] font-medium font-sans select-none text-[rgba(0,0,0,0.88)] hover:bg-[rgba(0,0,0,0.04)] cursor-pointer transition-colors',
                      isSelected && 'text-[#65558F]',
                      optionClassName
                    )}
                    onMouseDown={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleOptionSelect(option)
                    }}
                  >
                    <span className='truncate min-w-0'>{option.label}</span>
                    <div className='flex justify-center w-[24px] h-[20px] shrink-0'>
                      {isSelected && <SelectCheckIcon className='h-[20px] w-[20px]' />}
                    </div>
                  </div>
                )
              })}
              {displayedOptions.length === 0 && (
                <div className='py-4 text-center text-[14px] text-gray-400 select-none'>
                  No results found
                </div>
              )}
            </div>,
            document.body
          )}
      </div>
    )
  }
)

Selector.displayName = 'Selector'

// SelectItem component
export const SelectItem = React.forwardRef<HTMLDivElement, SelectorItemProps>(
  ({ value, children, disabled = false, className, ...props }, ref) => {
    // SelectItem mainly acts as a data container; the actual rendering is handled by the Select component.
    // It returns null because SelectItem does not render directly, it only passes data through.
    return null
  }
)

SelectItem.displayName = 'SelectorItem'
